import { Signal, computed, signal } from "@preact/signals";
import {
    isAndroid as getIsAndroid,
    isIOS as getIsIOS,
    isMobile as getIsMobile,
} from "~/utils";
import { MobileBackdropStyle, MobileMenuIcon } from "./interfaces";

const BACKDROP_CLOSE_INTENT_MS = 500;

const getIsTrackerPage = () => {
    const params = new URLSearchParams(window.location.search);

    return params.get("tracker") === "true";
};

const makeMobile = () => {
    const focus = signal(false);
    const dropdownStyles = signal<(MobileBackdropStyle | undefined)[]>([]);
    const dropdownSignals = signal<Signal<boolean>[]>([]);
    const hideHeaderRequests = signal<number>(0);
    const isIOS = getIsIOS();
    const isMobile = getIsMobile();
    const isAndroid = getIsAndroid();
    const isDropdownOpen = computed(() =>
        dropdownSignals.value.map((s) => s.value).some(Boolean),
    );
    const isMenuOpen = signal(false);
    const isMenuPage = signal(false);
    const isTrackerOpen = signal(false);
    const isTrackerPage = signal(getIsTrackerPage());
    const isBackdropLocked = signal(false);
    const isBackdropCloseIntent = signal(false);
    const dropdownStyle = computed(
        () => dropdownStyles.value.findLast(Boolean) || "light",
    );
    const isHeaderHidden = computed(() => hideHeaderRequests.value > 0);
    const menuIcon = signal<MobileMenuIcon>("hamburger");
    const menuHeadline = signal<string | null>(null);
    let menuListeners: (() => boolean | void)[] = [];
    let menuId = 0;
    let isNextTouchCancelled = false;

    window.addEventListener(
        "touchend",
        (event) => {
            if (isNextTouchCancelled) {
                isNextTouchCancelled = false;
                event.preventDefault();
                event.stopPropagation();
            }
        },
        { capture: true, passive: false },
    );

    const toggleMenu = (active?: boolean) => {
        const newState = active ?? !isMenuOpen.peek();

        isMenuOpen.value = newState;
        isTrackerOpen.value = false;
        isTrackerPage.value = false;
    };

    const toggleMenuPage = (active?: boolean) => {
        const newState = active ?? !isMenuPage.peek();

        isMenuPage.value = newState;
        isMenuOpen.value = false;
        isTrackerOpen.value = false;
    };

    const toggleTracker = (active?: boolean) => {
        if (isTrackerPage.peek()) {
            isTrackerPage.value = false;
        } else {
            const newState = active ?? !isTrackerOpen.peek();

            if (newState) {
                impact("light");
            }

            isTrackerOpen.value = newState;
        }
    };

    const toggleTrackerPage = (active?: boolean) => {
        const newState = active ?? !isTrackerPage.peek();

        isTrackerPage.value = newState;
        isMenuOpen.value = false;
        isTrackerOpen.value = false;
    };

    const intentBackdropClose = () => {
        if (isBackdropCloseIntent.peek()) {
            return;
        }

        isBackdropCloseIntent.value = true;

        window.setTimeout(
            () => (isBackdropCloseIntent.value = false),
            BACKDROP_CLOSE_INTENT_MS,
        );

        // haptic feedback for shake animation
        //
        window.setTimeout(
            () => impact("light"),
            BACKDROP_CLOSE_INTENT_MS * 0.25,
        );
        window.setTimeout(
            () => impact("light"),
            BACKDROP_CLOSE_INTENT_MS * 0.5,
        );
        window.setTimeout(
            () => impact("light"),
            BACKDROP_CLOSE_INTENT_MS * 0.75,
        );
    };

    const closeBackdrop = () => {
        if (isBackdropLocked.peek()) {
            intentBackdropClose();
            return;
        }

        isMenuOpen.value = false;
        isTrackerOpen.value = false;
        dropdownSignals.value.forEach((signal) => {
            if (signal.peek()) {
                signal.value = false;
            }
        });
    };

    const registerDropdown = (
        signal: Signal<boolean>,
        style?: MobileBackdropStyle,
    ) => {
        dropdownSignals.value = [...dropdownSignals.peek(), signal];

        let unsubscribe = () => {};

        if (style) {
            const myIndex = dropdownSignals.peek().length;

            unsubscribe = signal.subscribe((value) => {
                toggleBackdropStyle(myIndex, style, value);
            });

            if (signal.peek()) {
                toggleBackdropStyle(myIndex, style, true);
            }
        }

        return () => {
            const newValue = dropdownSignals.peek().slice();
            const index = newValue.indexOf(signal);

            if (index !== -1) {
                newValue.splice(index, 1);
                dropdownSignals.value = newValue;
            }

            unsubscribe();

            if (style) {
                toggleBackdropStyle(index, style, false);
            }
        };
    };

    const toggleBackdropStyle = (
        index: number,
        style: MobileBackdropStyle,
        active: boolean,
    ) => {
        const styles = dropdownStyles.peek().slice();

        styles[index] = active ? style : undefined;

        dropdownStyles.value = styles;
    };

    const hideHeader = (documentClass?: string) => {
        hideHeaderRequests.value += hideHeaderRequests.peek() + 1;

        if (documentClass) {
            document.documentElement.classList.add(documentClass);
        }

        let called = false;

        return () => {
            if (called) {
                return;
            }

            if (documentClass) {
                document.documentElement.classList.remove(documentClass);
            }

            called = true;
            hideHeaderRequests.value = hideHeaderRequests.peek() - 1;
        };
    };

    const setMenuAction = (
        icon: MobileMenuIcon,
        headline: string | null,
        callback: () => boolean | void,
    ) => {
        const id = ++menuId;

        menuIcon.value = icon;
        menuHeadline.value = headline;
        menuListeners = [callback];

        impact("light");

        return () => {
            if (id !== menuId) {
                return;
            }

            menuIcon.value = "hamburger";
            menuHeadline.value = null;
            menuListeners = [];
        };
    };

    const onMenuAction = () => {
        impact("light");

        if (menuListeners.length > 0) {
            if (isTrackerPage.peek()) {
                isTrackerPage.value = false;
                return;
            }

            const clean = menuListeners.every((listener) => listener());

            if (clean) {
                menuListeners = [];
                menuIcon.value = "hamburger";
            }
        } else {
            toggleMenu();
        }
    };

    const toggleStatusBar = (visible: boolean) => {
        if (isIOS) {
            window.webkit?.messageHandlers?.ui?.postMessage(
                visible ? "status_on" : "status_off",
            );
        } else if (isAndroid) {
            if (visible) {
                window.Android?.showStatusBar?.();
            } else {
                window.Android?.hideStatusBar?.();
            }
        }
    };

    const impact = (type: "light" | "medium" | "heavy") => {
        if (isIOS) {
            window.webkit?.messageHandlers?.ui?.postMessage(`impact_${type}`);
        } else if (isAndroid) {
            window.Android?.performHaptic?.(
                type === "light" ? "TextHandleMove" : "LongPress",
            );
        }
    };

    const cancelNextTouch = () => {
        isNextTouchCancelled = true;
    };

    return {
        isMobile,
        isIOS,
        isAndroid,
        isDropdownOpen,
        isMenuOpen,
        isMenuPage,
        isTrackerOpen,
        isTrackerPage,
        isBackdropLocked,
        isBackdropCloseIntent,
        dropdownStyle,
        isHeaderHidden,
        menuIcon,
        menuHeadline,
        focus,
        setMenuAction,
        onMenuAction,
        closeBackdrop,
        cancelNextTouch,
        registerDropdown,
        impact,
        toggleMenu,
        toggleMenuPage,
        toggleTracker,
        toggleTrackerPage,
        toggleStatusBar,
        hideHeader,
    };
};

export default makeMobile;
